import { useState } from 'react'
import toast from 'react-hot-toast'
import { useEquipModule, useUnequipModule } from '@/api/hooks'
import type { EquippedModuleDto, ModuleCatalogDto, ResourcesDto, ShipDto } from '@/api/hooks'
import { useCountdown } from '@/hooks/useCountdown'
import { ModuleIcon, CombatIcon, AnchorIcon } from '@/components/game/GameIcons'
import { HintBubble, ResourceTally } from '@/components/game/ui'

/** Teintes sémantiques du build (validées en maquette) — combat vs utilitaire. */
const CAT_COLOR: Record<'combat' | 'utility', string> = {
  combat: '#E08A5B',
  utility: '#5FBDD0',
}

/** Icônes d'action (currentColor → pilotées par la classe text-*). */
function TrashIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
    </svg>
  )
}

function SwapIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 4 3 8l4 4M3 8h13M17 20l4-4-4-4M21 16H8" />
    </svg>
  )
}

/**
 * Refit duration label per ship type — mirrors Domain/Ship/ShipRefitDuration
 * (Brick 2 min / Frégate 5 min / Galion 15 min / Légendaire 30 min). Kept in
 * sync by hand for the picker's "{durée}" hint; the authoritative timer is set
 * server-side at launch.
 */
const REFIT_DURATION_LABEL: Record<string, string> = {
  brick: '2 min',
  fregate: '5 min',
  galion: '15 min',
  legendary: '30 min',
}

/** Modules équipés d'un même type regroupés (empilement → « ×N »). Partagé avec
 *  les pastilles du picker de flotte (shipPicker/ShipPickerRow). */
export type ModuleGroup = { type: string; label: string; category: 'combat' | 'utility'; count: number; instanceIds: number[] }

export function groupModules(modules: EquippedModuleDto[]): ModuleGroup[] {
  const groups: ModuleGroup[] = []
  for (const m of modules) {
    const g = groups.find((x) => x.type === m.type)
    if (g) {
      g.count += 1
      g.instanceIds.push(m.instanceId)
    } else {
      groups.push({ type: m.type, label: m.label, category: m.category, count: 1, instanceIds: [m.instanceId] })
    }
  }
  return groups
}

/**
 * Per-ship modules — a SINGLE global slot pool (fin des pools typés). N'importe
 * quel module s'empile jusqu'au nombre de slots de la coque ; la catégorie ne
 * sert plus qu'à teinter/illustrer (combat vs utilitaire), jamais à réserver un
 * slot. Une jauge de build rend la spécialisation lisible d'un coup d'œil.
 *
 * Le picker liste les types débloqués (catalog) : ceux qui empilent restent
 * proposés même déjà posés ; la Longue-vue (non-stackable) tombe une fois montée.
 * Quand le pool est plein, chaque groupe offre un « remplacer » (swap de
 * n'importe quel module — un seul pool). Équiper débite le coût de refonte par
 * classe (or + bois) + un temps à quai ; retirer ne coûte que du temps.
 *
 * `disabled` couvre tout état non-actionnable (in_mission / damaged /
 * under_construction / refitting) ; une refonte en cours affiche un compte à
 * rebours à la place des actions.
 */
export function ShipModuleSlots({
  ship,
  catalog,
  resources,
  disabled,
}: {
  ship: ShipDto
  catalog: ModuleCatalogDto[]
  resources: ResourcesDto | null
  disabled: boolean
}) {
  const equip = useEquipModule()
  const unequip = useUnequipModule()
  // The slot driving the open picker: an equipped instanceId to swap out, or null
  // for a plain equip into a free slot. Undefined = picker closed.
  const [picker, setPicker] = useState<{ replacing: number | null } | null>(null)

  const refitting = ship.status === 'refitting'
  if (refitting && ship.refitResolvesAt) {
    return <RefitProgress ship={ship} />
  }

  // Affordability of THIS hull's refit (equip cost is per-class, same for every
  // module type). Null resources (payload not loaded) → don't pre-empt; the
  // server validates and the error toast covers it.
  const canAfford =
    !resources || (resources.gold >= ship.refitCost.gold && resources.wood >= ship.refitCost.wood)

  const slot = ship.slots
  const free = slot.total - slot.used
  const groups = groupModules(ship.modules)
  const combatCount = ship.modules.filter((m) => m.category === 'combat').length
  const utilityCount = ship.modules.filter((m) => m.category === 'utility').length

  // Candidate TYPES: any unlocked type that STACKS (fit several copies) or isn't
  // fitted yet. Combat dice + percent modules stack; the Longue-vue drops out
  // once fitted (one-per-type).
  const candidates = catalog.filter((c) => c.stackable || !ship.modules.some((m) => m.type === c.type))

  // When REPLACING a fitted module, drop its OWN type from the picker — swapping a
  // module for the same type is a pointless (paid) no-op.
  const replacingType =
    picker?.replacing != null ? ship.modules.find((m) => m.instanceId === picker.replacing)?.type : undefined
  const pickerCandidates = replacingType ? candidates.filter((c) => c.type !== replacingType) : candidates

  const onEquip = (type: string, removedInstanceId: number | null) => {
    equip.mutate(
      { shipId: ship.id, type, removedInstanceId: removedInstanceId ?? undefined },
      { onError: (err) => toast.error(err.message || 'Installation impossible') },
    )
    setPicker(null)
  }

  const busy = disabled || equip.isPending || unequip.isPending
  // Le pool plein → pas de « + Équiper », mais chaque module peut être remplacé (swap).
  const canSwap = free === 0 && candidates.length > 0

  return (
    <div className="rounded-lg bg-ocean-900/40 border border-sand-500/10 p-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-ocean-200">
          <ModuleIcon className="w-4 h-4 text-sand-300" />
          Modules
        </span>
        <span className="text-[11px] text-ocean-300 tabular-nums">
          {slot.used}/{slot.total}
        </span>
      </div>

      <BuildGauge total={slot.total} combat={combatCount} utility={utilityCount} />

      {groups.map((g) => (
        <EquippedRow
          key={g.type}
          group={g}
          description={catalog.find((c) => c.type === g.type)?.description}
          showActions={!disabled}
          disabled={busy}
          canSwap={canSwap}
          onRemove={() => unequip.mutate({ shipId: ship.id, instanceId: g.instanceIds[g.instanceIds.length - 1] })}
          onReplace={() => setPicker({ replacing: g.instanceIds[g.instanceIds.length - 1] })}
        />
      ))}

      {/* Navire non à quai → section en LECTURE SEULE : pas de boutons fantômes
          (ils semblaient cliquables mais ne faisaient rien). Sinon, actions. */}
      {disabled ? (
        <p className="text-xs text-ocean-400 italic">Le navire doit être à quai pour changer ses modules.</p>
      ) : (
        <>
          {free > 0 && (
            <button
              type="button"
              onClick={() => setPicker({ replacing: null })}
              disabled={busy}
              className="w-full text-left text-sm text-sand-300 hover:text-sand-100 border border-dashed border-sand-500/30 rounded-lg px-3 py-1.5 disabled:opacity-50"
            >
              + Équiper
            </button>
          )}
          {picker && (
            <Picker
              candidates={pickerCandidates}
              refitCost={ship.refitCost}
              canAfford={canAfford}
              durationLabel={REFIT_DURATION_LABEL[ship.shipType] ?? '—'}
              disabled={busy}
              onPick={(type) => onEquip(type, picker.replacing)}
              onClose={() => setPicker(null)}
            />
          )}
        </>
      )}
    </div>
  )
}

/**
 * Jauge de build : la part combat / utilitaire / libre du pool, pour lire la
 * spécialisation du navire d'un coup d'œil (l'objet de la refonte slots globaux).
 */
function BuildGauge({ total, combat, utility }: { total: number; combat: number; utility: number }) {
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0)
  const free = Math.max(0, total - combat - utility)

  return (
    <div className="space-y-1">
      <div className="flex h-1.5 rounded-full overflow-hidden bg-ocean-700/40 border border-sand-500/10">
        {combat > 0 && <span style={{ width: `${pct(combat)}%`, backgroundColor: CAT_COLOR.combat }} />}
        {utility > 0 && <span style={{ width: `${pct(utility)}%`, backgroundColor: CAT_COLOR.utility }} />}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-ocean-300">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: CAT_COLOR.combat }} />
          Combat <b className="text-sand-300 tabular-nums">{combat}</b>
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: CAT_COLOR.utility }} />
          Utilitaire <b className="text-sand-300 tabular-nums">{utility}</b>
        </span>
        <span className="tabular-nums">
          Libre <b className="text-sand-300">{free}</b>
        </span>
      </div>
    </div>
  )
}

function EquippedRow({
  group,
  description,
  showActions,
  disabled,
  canSwap,
  onRemove,
  onReplace,
}: {
  group: ModuleGroup
  /** Effet du module (description du catalogue) — tooltip au survol du nom. */
  description?: string
  /** false quand le navire n'est pas à quai → ligne en lecture seule (aucun bouton). */
  showActions: boolean
  disabled: boolean
  canSwap: boolean
  onRemove: () => void
  onReplace: () => void
}) {
  const Icon = group.category === 'combat' ? CombatIcon : AnchorIcon
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg bg-ocean-800/40 border border-sand-500/15 px-2.5 py-1.5">
      <span className="relative group/tip flex items-center gap-1.5 min-w-0">
        <span className="shrink-0 inline-flex" style={{ color: CAT_COLOR[group.category] }}>
          <Icon className="w-4 h-4" />
        </span>
        <span className="text-sm text-sand-100 truncate">
          {group.label}
          {group.count > 1 ? ` ×${group.count}` : ''}
        </span>
        {description && <HintBubble label={description} align="start" />}
      </span>
      {showActions && (
        <span className="flex items-center gap-1 shrink-0">
          {/* Remplacer = échange contre un autre type en 1 refonte (pool plein). */}
          {canSwap && (
            <button
              type="button"
              onClick={onReplace}
              disabled={disabled}
              aria-label="Remplacer par un autre module"
              title="Remplacer par un autre module"
              className="p-1 rounded text-ocean-300 hover:text-sand-100 hover:bg-ocean-700/40 disabled:opacity-40"
            >
              <SwapIcon className="w-4 h-4" />
            </button>
          )}
          {/* Retirer = enlève le module (libère le slot). */}
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            aria-label="Retirer"
            title="Retirer"
            className="p-1 rounded text-red-400 hover:text-red-300 hover:bg-ocean-700/40 disabled:opacity-40"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </span>
      )}
    </div>
  )
}

function Picker({
  candidates,
  refitCost,
  canAfford,
  durationLabel,
  disabled,
  onPick,
  onClose,
}: {
  candidates: ModuleCatalogDto[]
  refitCost: { gold: number; wood: number }
  canAfford: boolean
  durationLabel: string
  disabled: boolean
  onPick: (type: string) => void
  onClose: () => void
}) {
  return (
    <div className="rounded-lg bg-ocean-950/60 border border-sand-500/20 p-2 space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-ocean-300">Choisir un module</span>
        <button type="button" onClick={onClose} className="text-[11px] text-ocean-400 hover:text-ocean-200">
          fermer
        </button>
      </div>
      {candidates.length === 0 ? (
        <p className="text-xs text-ocean-400 italic">Aucun module compatible.</p>
      ) : (
        candidates.map((c) =>
          c.recipeUnlocked ? (
            <button
              key={c.type}
              type="button"
              onClick={() => onPick(c.type)}
              disabled={disabled || !canAfford}
              title={canAfford ? undefined : 'Ressources insuffisantes'}
              className="relative group/tip w-full flex items-center justify-between gap-2 rounded-md bg-ocean-800/50 hover:bg-ocean-700/60 border border-sand-500/10 px-2.5 py-1.5 text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="flex items-center gap-1.5 min-w-0">
                <span className="shrink-0 inline-flex" style={{ color: CAT_COLOR[c.category] }}>
                  {c.category === 'combat' ? <CombatIcon className="w-4 h-4" /> : <AnchorIcon className="w-4 h-4" />}
                </span>
                <span className="text-sm text-sand-100 truncate">{c.label}</span>
              </span>
              <HintBubble label={c.description} align="start" />
              <span className="flex items-center gap-2 shrink-0">
                <ResourceTally
                  gold={refitCost.gold || undefined}
                  wood={refitCost.wood || undefined}
                  className={`text-[11px] ${canAfford ? 'text-ocean-100' : 'text-red-400'}`}
                />
                <span className="text-[11px] text-ocean-300 tabular-nums">{durationLabel}</span>
              </span>
            </button>
          ) : (
            <LockedCandidate key={c.type} module={c} />
          ),
        )
      )}
    </div>
  )
}

function LockedCandidate({ module }: { module: ModuleCatalogDto }) {
  const reason = lockReason(module)
  // Verrouillé mais documenté : le tooltip décrit quand même le bonus, pour que
  // le joueur sache ce qu'il débloque en allant à la Forge / en notoriété.
  // ⚠️ `opacity-60` grise le CONTENU uniquement, JAMAIS le wrapper : `opacity`
  // s'applique à tout le sous-arbre, donc l'appliquer au conteneur entraînerait
  // la HintBubble avec lui → tooltip translucide et illisible.
  return (
    <div className="relative group/tip w-full flex items-center justify-between gap-2 rounded-md bg-ocean-900/40 border border-sand-500/5 px-2.5 py-1.5 cursor-not-allowed">
      <span className="flex items-center gap-1.5 min-w-0 opacity-60">
        <ModuleIcon className="w-4 h-4 text-sand-500 shrink-0" />
        <span className="text-sm text-sand-300/70 truncate">{module.label}</span>
      </span>
      <span className="text-[11px] uppercase tracking-wider text-sand-500/70 shrink-0 opacity-60">🔒 {reason}</span>
      <HintBubble label={module.description} align="start" />
    </div>
  )
}

/**
 * The unlock hint shown on a greyed candidate. A Forge recipe points at the
 * Forge; a reputation recipe shows live "notoriété X/Y" progress ONCE the
 * captain is on the outlaw track (Corsaire/Pirate), and the generic
 * "Notoire hors-la-loi requis" otherwise — a lawful captain's magnitude would
 * mislead (it counts toward Marine/Guilde, which can never latch it).
 */
function lockReason(module: ModuleCatalogDto): string {
  if (module.unlockSource !== 'reputation') return 'À débloquer à la Forge'
  const p = module.unlockProgress
  if (p && p.outlawAligned) return `Notoriété ${p.current}/${p.required}`
  return 'Notoire hors-la-loi requis'
}

function RefitProgress({ ship }: { ship: ShipDto }) {
  const countdown = useCountdown(ship.refitResolvesAt)
  const action = ship.refit?.action
  const heading = action === 'unequip' ? 'RETRAIT' : 'INSTALLATION'
  const label = ship.refit?.label ?? ''

  return (
    <div className="bg-ocean-900/55 rounded-lg p-2.5 text-center border border-sand-500/10">
      <p className="text-[11px] text-ocean-300 uppercase tracking-wider">
        {heading}
        {label ? ` · ${label}` : ''}
      </p>
      <p className="font-display text-lg text-sand-100 tabular-nums">{countdown}</p>
    </div>
  )
}
